import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DeviceHubRoundedIcon from '@mui/icons-material/DeviceHubRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import HealthAndSafetyRoundedIcon from '@mui/icons-material/HealthAndSafetyRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import TextSnippetRoundedIcon from '@mui/icons-material/TextSnippetRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';

import api from '../../services/api';
import { DetailField, LoadingState, PageHeader, PageLayout, SectionCard } from '../../components/ui';

const PUNCH_TYPES = [
  'CheckIn',
  'CheckOut',
  'BreakIn',
  'BreakOut',
  'OverTimeIn',
  'OverTimeOut',
  'MealIn',
  'MealOut',
];

const INPUT_TYPES = [
  'Fingerprint',
  'Face',
  'Card',
  'Palm',
  'Vein',
  'Retina',
  'Password',
];

const initialForm = {
  employee_code: '',
  device_stgid: '',
  log_time: '',
  type: 'CheckIn',
  input_type: 'Fingerprint',
  temperature: '',
  mask: false,
  operation_id: '',
  raw_payload: '',
};

const normalizeDateTimeLocal = (value) => {
  if (!value) return '';
  const text = String(value).replace(' ', 'T');
  const match = text.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return match?.[1] || text;
};

const buildPayload = (form) => ({
  employee_code: form.employee_code,
  device_stgid: form.device_stgid,
  log_time: form.log_time,
  type: form.type,
  input_type: form.input_type,
  temperature: form.temperature || null,
  mask: form.mask,
  operation_id: form.operation_id || null,
  raw_payload: form.raw_payload || null,
});

function FieldGroup({ icon, title, description, children }) {
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
        {icon}
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
            {title}
          </Typography>
          {description ? (
            <Typography variant="caption" color="text.secondary">
              {description}
            </Typography>
          ) : null}
        </Box>
      </Stack>
      {children}
    </Box>
  );
}

function FieldGrid({ children }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.2,
      }}
    >
      {children}
    </Box>
  );
}

function SummaryRow({ icon, label, value }) {
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ color: 'text.secondary', display: 'grid', placeItems: 'center', mt: 0.1 }}>{icon}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 850, overflowWrap: 'anywhere' }}>
          {value || '-'}
        </Typography>
      </Box>
    </Stack>
  );
}

function AttendanceFormPage({ isEdit }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const [form, setForm] = useState(initialForm);
  const [originalForm, setOriginalForm] = useState(initialForm);
  const [loading, setLoading] = useState(Boolean(isEdit && id));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const requiredStatus = useMemo(
    () => [
      { label: 'Employee code', done: Boolean(form.employee_code) },
      { label: 'Device STGID', done: Boolean(form.device_stgid) },
      { label: 'Log time', done: Boolean(form.log_time) },
      { label: 'Punch type', done: Boolean(form.type) },
      { label: 'Input type', done: Boolean(form.input_type) },
    ],
    [form.device_stgid, form.employee_code, form.input_type, form.log_time, form.type]
  );

  const completionPct = Math.round((requiredStatus.filter((item) => item.done).length / requiredStatus.length) * 100);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.hr?.radius?.md || 2,
      backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
    },
  };

  useEffect(() => {
    if (!isEdit || !id) return;

    const fetchAttendance = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await api.get(`/attendances/${id}`);
        const data = response.data || {};
        const nextForm = {
          employee_code: data.employee_code || '',
          device_stgid: data.device_stgid || '',
          log_time: normalizeDateTimeLocal(data.log_time),
          type: data.type || 'CheckIn',
          input_type: data.input_type || 'Fingerprint',
          temperature: data.temperature || '',
          mask: Boolean(data.mask),
          operation_id: data.operation_id || '',
          raw_payload: data.raw_payload ? JSON.stringify(data.raw_payload, null, 2) : '',
        };
        setForm(nextForm);
        setOriginalForm(nextForm);
      } catch (fetchError) {
        console.error('Error fetching attendance:', fetchError);
        setError('Failed to load attendance record.');
      } finally {
        setLoading(false);
      }
    };

    fetchAttendance();
  }, [id, isEdit]);

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleReset = () => {
    setError('');
    setForm(isEdit ? originalForm : initialForm);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = buildPayload(form);
      if (isEdit && id) {
        await api.put(`/attendances/${id}`, payload);
      } else {
        await api.post('/attendances', payload);
      }
      navigate('/admin/attendances');
    } catch (submitError) {
      console.error(submitError);
      setError(submitError.response?.data?.error || submitError.response?.data?.message || 'Error saving data');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageLayout backgroundAccent compact maxWidth="xl">
      <PageHeader
        compact
        variant="dashboard"
        overline="Attendance"
        title={isEdit ? `Edit Attendance #${id}` : 'Add Attendance Record'}
        subtitle="Create or correct biometric attendance records while preserving the device payload fields used by payroll and audits."
        actions={[
          <Button
            key="back"
            variant="outlined"
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() => navigate('/admin/attendances')}
          >
            Back to Attendance
          </Button>,
        ]}
      />

      {loading ? (
        <LoadingState rows={8} />
      ) : (
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) 360px' },
            gap: 1.5,
            alignItems: 'start',
          }}
        >
          <SectionCard
            tone="dashboard"
            title="Punch Record"
            subheader="Required fields identify the employee, source device, log time, and punch classification."
            contentSx={{ p: { xs: 1.35, md: 1.7 } }}
          >
            <Stack spacing={1.8}>
              {error ? <Alert severity="error">{error}</Alert> : null}

              <FieldGroup
                icon={<BadgeOutlinedIcon color="primary" />}
                title="Employee and Device"
                description="Use the employee code and device STGID exactly as provided by the attendance source."
              >
                <FieldGrid>
                  <TextField
                    label="Employee Code"
                    value={form.employee_code}
                    onChange={(event) => updateField('employee_code', event.target.value)}
                    required
                    sx={fieldSx}
                  />
                  <TextField
                    label="Device STGID"
                    value={form.device_stgid}
                    onChange={(event) => updateField('device_stgid', event.target.value)}
                    required
                    sx={fieldSx}
                  />
                </FieldGrid>
              </FieldGroup>

              <Divider />

              <FieldGroup
                icon={<ScheduleRoundedIcon color="primary" />}
                title="Log Time and Type"
                description="Classify the punch so downstream attendance calculations remain correct."
              >
                <FieldGrid>
                  <TextField
                    label="Log Time"
                    type="datetime-local"
                    value={form.log_time}
                    onChange={(event) => updateField('log_time', event.target.value)}
                    required
                    InputLabelProps={{ shrink: true }}
                    sx={fieldSx}
                  />
                  <FormControl fullWidth required size="small" sx={fieldSx}>
                    <InputLabel id="attendance-type-label">Type</InputLabel>
                    <Select
                      labelId="attendance-type-label"
                      label="Type"
                      value={form.type}
                      onChange={(event) => updateField('type', event.target.value)}
                    >
                      {PUNCH_TYPES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth required size="small" sx={fieldSx}>
                    <InputLabel id="input-type-label">Input Type</InputLabel>
                    <Select
                      labelId="input-type-label"
                      label="Input Type"
                      value={form.input_type}
                      onChange={(event) => updateField('input_type', event.target.value)}
                    >
                      {INPUT_TYPES.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Operation ID"
                    value={form.operation_id}
                    onChange={(event) => updateField('operation_id', event.target.value)}
                    sx={fieldSx}
                  />
                </FieldGrid>
              </FieldGroup>

              <Divider />

              <FieldGroup
                icon={<HealthAndSafetyRoundedIcon color="primary" />}
                title="Device Metadata"
                description="Optional biometric metadata from imported device records."
              >
                <FieldGrid>
                  <TextField
                    label="Temperature"
                    type="number"
                    value={form.temperature}
                    onChange={(event) => updateField('temperature', event.target.value)}
                    inputProps={{ step: '0.01' }}
                    sx={fieldSx}
                  />
                  <Box
                    sx={{
                      px: 1.4,
                      minHeight: 42,
                      display: 'flex',
                      alignItems: 'center',
                      borderRadius: theme.hr?.radius?.md || 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === 'dark' ? 0.74 : 0.92),
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={form.mask}
                          onChange={(event) => updateField('mask', event.target.checked)}
                        />
                      }
                      label="Mask detected"
                    />
                  </Box>
                </FieldGrid>
              </FieldGroup>

              <Divider />

              <FieldGroup
                icon={<TextSnippetRoundedIcon color="primary" />}
                title="Raw Payload"
                description="Stored as provided by the device integration. Keep JSON formatting if present."
              >
                <TextField
                  label="Raw Payload"
                  value={form.raw_payload}
                  onChange={(event) => updateField('raw_payload', event.target.value)}
                  multiline
                  minRows={5}
                  sx={fieldSx}
                />
              </FieldGroup>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={handleReset}
                  disabled={saving}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveRoundedIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEdit ? 'Update Attendance' : 'Create Attendance'}
                </Button>
              </Stack>
            </Stack>
          </SectionCard>

          <Stack spacing={1.5} sx={{ position: { lg: 'sticky' }, top: { lg: 88 } }}>
            <SectionCard
              tone="dashboard"
              title="Record Summary"
              subheader="Live quality check before saving."
              contentSx={{ p: 1.6 }}
            >
              <Stack spacing={1.25}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip
                    size="small"
                    color={completionPct === 100 ? 'success' : 'warning'}
                    label={`${completionPct}% complete`}
                  />
                  <Chip size="small" variant="outlined" label={isEdit ? 'Edit mode' : 'Create mode'} />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completionPct}
                  sx={{ height: 8, borderRadius: 999, bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                />
                <SummaryRow
                  icon={<BadgeOutlinedIcon fontSize="small" />}
                  label="Employee Code"
                  value={form.employee_code}
                />
                <SummaryRow
                  icon={<DeviceHubRoundedIcon fontSize="small" />}
                  label="Device STGID"
                  value={form.device_stgid}
                />
                <SummaryRow
                  icon={<ScheduleRoundedIcon fontSize="small" />}
                  label="Log Time"
                  value={form.log_time}
                />
                <SummaryRow
                  icon={<TouchAppRoundedIcon fontSize="small" />}
                  label="Punch"
                  value={`${form.type || '-'} / ${form.input_type || '-'}`}
                />
                <DetailField
                  compact
                  label="Optional Metadata"
                  value={`Temp: ${form.temperature || '-'} | Mask: ${form.mask ? 'Yes' : 'No'} | Operation: ${form.operation_id || '-'}`}
                  icon={<FingerprintRoundedIcon fontSize="small" />}
                />
              </Stack>
            </SectionCard>
          </Stack>
        </Box>
      )}
    </PageLayout>
  );
}

export default AttendanceFormPage;
